/*
 * From http://www.redblobgames.com/x/1740-map-painter/
 * Copyright 2017 Red Blob Games <redblobgames@gmail.com>
 * License: Apache v2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 * Page made for Nonagon Games
 */
'use strict';

/* global Vue */

const DualMesh = require('@redblobgames/dual-mesh');
const createMesh = require('@redblobgames/dual-mesh/create');
const prng = require('@redblobgames/prng');
const PointInRegion = require('point-in-region');
const _ = require('./vue-components');

const mesh = new DualMesh(createMesh({spacing: 50, random: prng.makeRandFloat(12345)}));

const colors = {
    grass: "hsl(120,20%,50%)",
    rock: "hsl(60,10%,70%)",
    water: "hsl(220,50%,30%)",
};

function initialMap() {
    let map = new Array(mesh.numRegions);
    map.fill('grass');
    for (let r = 0; r < mesh.numRegions; r++) {
        if (mesh.r_boundary(r)) { map[r] = 'water'; }
    }
    return map;
}

// NOTE: the point-in-region library requires a nested array, and not
// the flat array I use, so I need to convert it here
let t_vertex = [];
for (let t = 0; t < mesh.numTriangles; t++) {
    t_vertex.push([mesh.t_x(t), mesh.t_y(t)]);
}
const pointToRegion = PointInRegion(t_vertex, (() => {
    let polygons = [];
    for (let r = 0; r < mesh.numSolidRegions; r++) {
        polygons.push([mesh.r_circulate_t([], r)]);
    }
    return polygons;
})());


/* The map drawing component attaches code to redraw a map onto a canvas */
Vue.component('the-map', {
    props: ['mesh', 'r_biome'],
    template: `<a-canvas :width="1000" height="1000" :draw="this.redraw">`,
    methods: {
        redraw(canvas, ctx) {
            let out_s = [];
            function drawRegion(r) {
                mesh.r_circulate_s(out_s, r);
                let last_t = mesh.s_inner_t(out_s[0]);
                ctx.beginPath();
                ctx.moveTo(mesh.t_x(last_t), mesh.t_y(last_t));
                for (let s of out_s) {
                    let first_t = mesh.s_outer_t(s);
                    ctx.lineTo(mesh.t_x(first_t), mesh.t_y(first_t));
                }
            }
            
            ctx.save();
            for (let r = 0; r < mesh.numSolidRegions; r++) {
                ctx.fillStyle = colors[this.r_biome[r]];
                drawRegion(r);
                ctx.fill();
            }
            ctx.strokeStyle = "black";
            ctx.lineWidth = 0.5;
            for (let r = 0; r < mesh.numSolidRegions; r++) {
                drawRegion(r);
                ctx.stroke();
            }
            ctx.restore();
        },
    },
});


/* Main UI */
let editor = new Vue({
    el: "#vue-el",
    data: {
        mesh: Object.freeze(Object.create(mesh)),
        tool: 'rock',
        buttons: ['grass', 'rock', 'water'],
        r_biome: initialMap(),
    },
    computed: {
        json() {
            let r_vertices_t = [];
            for (let r = 0; r < mesh.numSolidRegions; r++) {
                r_vertices_t.push(mesh.r_circulate_t([], r));
            }
            return JSON.stringify({r_vertices_t, t_vertex, r_biome: this.r_biome}, null, " ");
        },
    },
    methods: {
        start(operation, event) {
            operation.x = event.x;
            operation.y = event.y;
        },
        drag(operation, event) {
            let distance = Math.abs(event.x - operation.x) + Math.abs(event.y - operation.y);
            for (let i = 0; i <= distance; i += 15) {
                let t = i / distance;
                let x = t * event.x + (1-t) * operation.x;
                let y = t * event.y + (1-t) * operation.y;
                this.paint(
                    x * 1000 / operation.el.offsetWidth,
                    y * 1000 / operation.el.offsetHeight,
                    editor.tool
                );
            }
            operation.x = event.x;
            operation.y = event.y;
        },
        paint(x, y, tool) {
            let r = pointToRegion([x, y]);
            if (r >= 0 && this.r_biome[r] !== this.tool) {
                // NOTE: Vue doesn't optimize away setting a property
                // to its current value
                Vue.set(this.r_biome, r, this.tool);
            }
        }
    },
});
